import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, ImageOff, Pencil } from "lucide-react";

import { archiveInventory } from "src/api/inventory";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { renderCurrency, showSuccessToast } from "src/lib/utils";
import { stockStatusOf } from "src/types/inventory";
import { StatusBadge } from "src/components/common";
import type { ProductRow } from "src/types/inventory";
import EditProductModal from "./EditProductModal";

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-border tw-py-2.5 last:tw-border-0">
    <span className="tw-text-[13px] tw-text-text-soft">{label}</span>
    <span className="tw-text-right tw-text-[13.5px] tw-font-medium tw-text-text">
      {value}
    </span>
  </div>
);

interface ProductDetailModalProps {
  product: ProductRow;
}

/** Product profile with an inline edit swap — the modal keeps its frame, the body switches. */
const ProductDetailModal = ({ product }: ProductDetailModalProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);

  return isEditOpen ? (
    <EditProductModal product={product} />
  ) : (
    <Details product={product} onEdit={() => setIsEditOpen(true)} />
  );
};

const Details = ({
  product,
  onEdit,
}: {
  product: ProductRow;
  onEdit: () => void;
}) => {
  const queryClient = useQueryClient();
  const closePortal = usePortalContext();

  const { mutate, isLoading } = useMutation(archiveInventory, {
    onSuccess: () => {
      showSuccessToast("Product archived successfully");
      queryClient.invalidateQueries(["inventory"]);
      closePortal();
    },
  });

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row">
        <div className="tw-flex tw-h-36 tw-w-full tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[10px] tw-border tw-border-border tw-bg-bg sm:tw-w-36">
          {product.image ? (
            <img
              className="tw-h-full tw-w-full tw-object-cover"
              src={product.image}
              alt={product.name ?? "Product"}
            />
          ) : (
            <ImageOff className="tw-size-6 tw-text-text-soft" />
          )}
        </div>

        <div className="tw-min-w-0 tw-flex-1">
          <h3 className="tw-text-[17px] tw-font-semibold tw-text-text">
            {product.name || "Untitled product"}
          </h3>
          <p className="tw-mt-0.5 tw-text-[13px] tw-text-text-soft">
            Item code {product.item_id || "N/A"}
          </p>
          <div className="tw-mt-2.5">
            <StatusBadge status={stockStatusOf(product.available_qty)} />
          </div>
        </div>
      </div>

      <div className="tw-rounded-[10px] tw-border tw-border-border tw-px-4">
        <DetailRow label="SKU" value={product.sku || "N/A"} />
        <DetailRow
          label="Available quantity"
          value={product.available_qty ?? 0}
        />
        <DetailRow
          label="Cost price"
          value={renderCurrency(product.cost_price)}
        />
        <DetailRow
          label="Selling price"
          value={renderCurrency(product.price)}
        />
      </div>

      <div>
        <h4 className="tw-mb-1 tw-text-[13px] tw-font-semibold tw-text-text">
          Description
        </h4>
        <p className="tw-text-[13.5px] tw-text-text-soft">
          {product.description || "No description available"}
        </p>
      </div>

      <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
        <Button
          type="button"
          variant="outline"
          className="tw-gap-1.5"
          onClick={onEdit}
        >
          <Pencil className="tw-size-4" />
          Edit product
        </Button>

        <CustomConfirmDialog
          header="Archive this product?"
          subHeader="It will be hidden from the inventory list. You can restore it from Archived products."
          confirmButtonText="Yes, Archive Inventory"
          trigger={
            <Button
              type="button"
              variant="outline"
              className="tw-gap-1.5 tw-text-red hover:tw-text-red"
              disabled={isLoading}
            >
              <Archive className="tw-size-4" />
              Archive product
            </Button>
          }
          mutateFn={() => mutate({ uuid: product.uuid })}
        />
      </div>
    </div>
  );
};

export default ProductDetailModal;
